import {
  E,
  clamp,
  numeric,
  short,
  text,
  line,
  rect,
  circle,
  path,
  point,
  sample,
  numberValue,
  reading,
  tone,
  simple,
  svgId,
  part,
} from "./render-kit.js";
import {
  convert,
  dimensionForUnit,
  smoothSeries,
  formatReading,
} from "./units.js";

export const contextRendererIds = Object.freeze([
  "sparkline",
  "history",
  "depth-graph",
  "state-history",
  "comparison-bars",
  "multi-graph",
  "anchor",
  "gps",
  "signal",
  "board-health",
  "traffic",
  "route",
  "forecast",
  "tide",
  "moon",
  "pressure",
  "power-budget",
  "engine-vitals",
  "tanks-budget",
  "water-budget",
  "depth-safety",
  "environment",
  "changes",
]);

const C = {
  accent: "var(--accent)",
  text: "var(--text)",
  muted: "var(--muted)",
  quiet: "var(--quiet)",
  grid: "var(--grid)",
  surface: "var(--surface)",
};
const usable = (p) =>
  numeric(p?.value) && (!p.quality || ["good", "stale"].includes(p.quality));
const fixed = (n, decimals = 1) => (numeric(n) ? n.toFixed(decimals) : "—");
const dateText = (time, o = {}) => {
  if (!numeric(time)) return "Unavailable";
  const date = new Date(time);
  if (!Number.isFinite(date.getTime())) return "Unavailable";
  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: o.timezone ?? "UTC",
  }).format(date);
};
const windowMs = (o) =>
  (/^\d+h$/.test(o.window ?? "")
    ? Number(o.window.slice(0, -1)) * 3600000
    : null) ??
  { "1h": 3600000, "24h": 86400000, "7d": 604800000, "30d": 2592000000 }[
    o.window ?? o.historyWindow
  ] ??
  86400000;
const noData = (label) =>
  text(200, 143, "—", 56, C.quiet, "middle", part("value")) +
  text(200, 177, label, 12, C.muted, "middle", part("caption"));
const label = (x, y, value, anchor = "start") =>
  text(
    x,
    y,
    short(value, 44),
    10,
    C.muted,
    anchor,
    part("caption", 'letter-spacing=".4"'),
  );
const badge = (x, y, value, color = C.muted, width = 86) =>
  rect(x, y - 14, width, 24, C.surface, 12, part("badge")) +
  text(
    x + width / 2,
    y + 2,
    short(value, 19),
    10,
    color,
    "middle",
    part("badge"),
  );
const metric = (data, role, o, x, y, size = 42, anchor = "start") => {
  const f = reading(data, role, o);
  return (
    text(
      x,
      y,
      f.value,
      size,
      f.quality === "stale" ? C.quiet : C.text,
      anchor,
      'font-weight="650" data-motion="value"',
    ) + text(x, y + 23, f.unit, 11, C.muted, anchor)
  );
};
const fieldLabel = (data, field) => sample(data, field)?.label ?? field;
const fieldText = (data, field, o) => {
  const f = reading(data, field, o);
  return `${f.value}${f.unit ? ` ${f.unit}` : ""}${sample(data, field)?.estimated ? " ≈" : ""}`;
};
const estimateNote = (data) => {
  const values = data.service?.values ?? data.values ?? {};
  return Object.values(values).some((r) => r?.estimated)
    ? `≈ Estimate · ${data.service?.confidence ?? "confidence unavailable"}`
    : "";
};
const notice = (value, y = 250, color = C.muted) =>
  label(24, y, value).replace(`fill="${C.muted}"`, `fill="${color}"`);
function entriesFor(data, o) {
  const order = o.panelOrder ?? [];
  return Object.entries(data.history ?? {})
    .filter(([, s]) => Array.isArray(s?.points))
    .sort(([a], [b]) => {
      const aa = order.indexOf(a),
        bb = order.indexOf(b);
      return (aa < 0 ? Infinity : aa) - (bb < 0 ? Infinity : bb);
    })
    .slice(0, o.limit ?? 6);
}
function prepareSeries(data, o, entries) {
  return entries.map(([role, series]) => {
    const times = series.points.map((p) => p.time).filter(numeric);
    const now = numeric(data.now)
      ? data.now
      : times.length
        ? Math.max(...times)
        : null;
    const unit =
      o.roleUnits?.[role] ??
      (o.unit && o.unit !== "inherit" ? o.unit : series.unit);
    const compatible =
      !series.unit ||
      !unit ||
      unit === "inherit" ||
      series.unit === unit ||
      (Boolean(dimensionForUnit(series.unit)) &&
        dimensionForUnit(series.unit) === dimensionForUnit(unit));
    const selectedUnit = compatible && unit !== "inherit" ? unit : series.unit;
    const circular =
      series.reference === "bearing" || o.aggregation === "circular";
    let points = smoothSeries(
      series.points.filter(
        (p) =>
          numeric(p.time) &&
          (!o.rolling || !numeric(now) || p.time >= now - windowMs(o)),
      ),
      o.smoothing,
      circular,
    ).map((p) => ({
      ...p,
      value:
        usable(p) && series.unit && selectedUnit && selectedUnit !== "inherit"
          ? convert(p.value, series.unit, selectedUnit)
          : p.value,
    }));
    if (circular && dimensionForUnit(selectedUnit) === "angle") {
      const revolution = selectedUnit === "rad" ? Math.PI * 2 : 360;
      let previous = null;
      points = points.map((p) => {
        if (!usable(p)) {
          previous = null;
          return p;
        }
        let value = p.value;
        if (numeric(previous))
          value =
            previous +
            ((value - previous + revolution * 1.5) % revolution) -
            revolution / 2;
        previous = value;
        return { ...p, value };
      });
    }
    return [role, { ...series, unit: selectedUnit, points }];
  });
}
function historyPlot(
  data,
  o,
  {
    entries = entriesFor(data, o),
    x = 46,
    y = 141,
    w = 322,
    h = 83,
    axes = !simple(o),
    depth = false,
    fill = !simple(o),
    legend = true,
    tide = false,
    timeline = true,
  } = {},
) {
  if (!entries.length) return "";
  const dimensions = entries.map(
    ([, s]) => dimensionForUnit(s.unit) ?? s.unit ?? "none",
  );
  if (new Set(dimensions).size > 1)
    return label(x, y + h / 2, "Use aligned panels for mixed units");
  const prepared = prepareSeries(data, o, entries);
  const points = prepared.flatMap(([, s]) => s.points);
  const valid = points.filter(usable);
  if (!valid.length) return label(x, y + h / 2, "No usable history");
  let min = Math.min(...valid.map((p) => p.value)),
    max = Math.max(...valid.map((p) => p.value));
  if (o.domain === "zero" || o.baseline === "zero") min = Math.min(0, min);
  if (o.scaleMode === "fixed" || o.domain === "fixed") {
    min = o.scaleMin;
    max = o.scaleMax;
  }
  if (tide && max - min < (o.minRangeMeters ?? 0)) {
    const unit = prepared[0][1].unit;
    const range =
      dimensionForUnit(unit) === "length"
        ? convert(o.minRangeMeters, "m", unit)
        : o.minRangeMeters;
    max = min + range;
  }
  if (!numeric(min) || !numeric(max) || min > max)
    return label(x, y + h / 2, "Scale unavailable");
  if (max === min) {
    min -= 1;
    max += 1;
  }
  const times = points.map((p) => p.time).filter(numeric);
  const t1 = numeric(data.now) ? data.now : Math.max(...times);
  const t0 = o.rolling ? t1 - windowMs(o) : Math.min(...times);
  const px = (t) => x + clamp((t - t0) / (t1 - t0 || 1), 0, 1) * w;
  const py = (v) =>
    y +
    (depth
      ? clamp((v - min) / (max - min), 0, 1)
      : 1 - clamp((v - min) / (max - min), 0, 1)) *
      h;
  const fillId = svgId(o, `history-${x}-${y}`);
  let out = `<defs><linearGradient id="${E(fillId)}" x1="0" y1="0" x2="0" y2="1"><stop stop-color="var(--accent)" stop-opacity=".24"/><stop offset="1" stop-color="var(--accent)" stop-opacity=".015"/></linearGradient></defs>`;
  if (axes) {
    for (let k = 0; k <= 2; k++) {
      const v = depth
        ? min + (k / 2) * (max - min)
        : max - (k / 2) * (max - min);
      out += line(
        x,
        y + (k * h) / 2,
        x + w,
        y + (k * h) / 2,
        C.grid,
        0.8,
        part("ticks"),
      );
      out += text(
        x - 7,
        y + (k * h) / 2 + 3,
        fixed(v),
        8,
        C.quiet,
        "end",
        part("tick-labels"),
      );
    }
  } else out += line(x, y + h, x + w, y + h, C.grid, 0.6, part("ticks"));
  const colors = [C.accent, C.text, C.quiet];
  prepared.forEach(([role, series], k) => {
    const segments = [];
    let current = [];
    for (const p of series.points) {
      if (!usable(p)) {
        if (current.length) segments.push(current);
        current = [];
      } else current.push(p);
    }
    if (current.length) segments.push(current);
    for (const segment of segments) {
      let d = "";
      segment.forEach((p, j) => {
        const xx = px(p.time),
          yy = py(p.value);
        if (j === 0) d = `M ${xx} ${yy}`;
        else if (o.curve === "step") d += ` H ${xx} V ${yy}`;
        else if (o.curve === "smooth") {
          const prev = segment[j - 1],
            mid = (px(prev.time) + xx) / 2;
          d += ` C ${mid} ${py(prev.value)}, ${mid} ${yy}, ${xx} ${yy}`;
        } else d += ` L ${xx} ${yy}`;
      });
      if (fill || o.envelope || o.bottomFill) {
        const baseY = depth && o.bottomFill ? y + h : py(min);
        out += path(
          `${d} L ${px(segment.at(-1).time)} ${baseY} L ${px(segment[0].time)} ${baseY} Z`,
          "none",
          0,
          part(
            "history-area",
            `fill="${k === 0 ? `url(#${E(fillId)})` : colors[k % 3]}"${k ? ' opacity=".06"' : ""} data-motion="area"`,
          ),
        );
      }
      out += path(
        d,
        colors[k % 3],
        o.lineWidth ?? 2.5,
        part(
          "history-line",
          `${k ? `stroke-dasharray="${k === 1 ? "7 5" : "2 5"}"` : ""} data-motion="trace"`,
        ),
      );
      if (segment.length === 1)
        out += circle(
          px(segment[0].time),
          py(segment[0].value),
          2,
          colors[k % 3],
          "none",
          1,
          part("history-dot"),
        );
    }
    const last = series.points.at(-1);
    if (usable(last))
      out += circle(
        px(last.time),
        py(last.value),
        simple(o) ? 3 : 4,
        colors[k % 3],
        "var(--surface)",
        1,
        part("history-dot", 'data-motion="point"'),
      );
    if (
      legend &&
      o.legend !== "hide" &&
      (o.legend === "show" || prepared.length > 1)
    ) {
      const xx = x + (k * w) / prepared.length;
      out += line(
        xx,
        y + h + 17,
        xx + 10,
        y + h + 17,
        colors[k % 3],
        2,
        part("legend"),
      );
      out += text(
        xx + 16,
        y + h + 20,
        short(role, 15),
        9,
        C.muted,
        "start",
        part("legend"),
      );
    }
  });
  const markers = [
    ...(o.markers ?? []),
    ...(o.thresholds ?? []),
    ...(depth && numeric(o.shallowLimit)
      ? [
          {
            value: o.shallowLimit,
            label: "Visual shallow guide",
            tone: "muted",
          },
        ]
      : []),
  ];
  for (const m of markers) {
    if (!numeric(m.value) || m.value < min || m.value > max) continue;
    out += line(
      x,
      py(m.value),
      x + w,
      py(m.value),
      tone(m.tone),
      1,
      part("reference", 'stroke-dasharray="3 5"'),
    );
    out += text(
      x + w,
      py(m.value) - 5,
      short(m.label, 30),
      8,
      C.muted,
      "end",
      part("caption"),
    );
  }
  if (o.extremes !== "off" || (tide && o.showExtrema))
    out += text(
      x,
      y - 9,
      `Min ${fixed(Math.min(...valid.map((p) => p.value)))} · Max ${fixed(Math.max(...valid.map((p) => p.value)))}`,
      9,
      C.muted,
      "start",
      part("caption"),
    );
  if (o.endpointLabel && usable(prepared[0][1].points.at(-1)))
    out += text(
      x + w,
      y - 9,
      fixed(prepared[0][1].points.at(-1).value),
      10,
      C.accent,
      "end",
      part("secondary"),
    );
  if (timeline && (o.legend === "hide" || prepared.length === 1)) {
    out += text(
      x,
      y + h + 20,
      dateText(t0, o),
      8,
      C.quiet,
      "start",
      part("tick-labels"),
    );
    out += text(
      x + w,
      y + h + 20,
      dateText(t1, o),
      8,
      C.quiet,
      "end",
      part("tick-labels"),
    );
  }
  return out;
}
function latestHistory(data, o) {
  const entry = entriesFor(data, o)[0];
  if (!entry) return null;
  const [role, s] = entry,
    last = s.points.at(-1);
  if (!last) return null;
  return { role, sample: { ...last, unit: s.unit }, series: s };
}
function graphFace(id, data, o) {
  if (id === "comparison-bars") return comparisonBars(data, o);
  if (id === "state-history") return stateHistory(data, o);
  const entries = entriesFor(data, o);
  if (!entries.length) return noData("History source unavailable");
  if (id === "multi-graph") {
    const rowH = 185 / entries.length;
    let out = "";
    entries.forEach(([role, series], k) => {
      const y = 57 + k * rowH;
      const last = series.points.at(-1);
      const f = formatReading(last ? { ...last, unit: series.unit } : null, {
        ...o,
        unit: o.roleUnits?.[role] ?? "inherit",
      });
      out += label(24, y + 9, role);
      out += text(
        24,
        y + 29,
        `${f.value} ${f.unit}`,
        entries.length > 4 ? 12 : 16,
        C.text,
        "start",
        'font-weight="600"',
      );
      const custom = {
        ...o,
        legend: "hide",
        extremes: "off",
        ...(o.domains?.[role]
          ? {
              scaleMode: "fixed",
              scaleMin: o.domains[role][0],
              scaleMax: o.domains[role][1],
            }
          : {}),
      };
      out += historyPlot(data, custom, {
        entries: [[role, series]],
        x: 133,
        y: y + 5,
        w: 239,
        h: Math.max(15, rowH - 13),
        axes: false,
        timeline: false,
        legend: false,
      });
      if (!simple(o))
        out += line(24, y + rowH - 2, 376, y + rowH - 2, C.grid, 0.6);
    });
    if (o.sharedTimeAxis)
      out += notice("Aligned time · independent units", 254);
    return out;
  }
  const dims = new Set(
    entries.map(([, s]) => dimensionForUnit(s.unit) ?? s.unit ?? "none"),
  );
  if (dims.size > 1) return noData("Use aligned panels for mixed units");
  const latest = latestHistory(data, o);
  const f = latest
    ? reading({ values: { [latest.role]: latest.sample } }, latest.role, o)
    : { value: "—", unit: "" };
  let out = label(
    24,
    70,
    id === "depth-graph"
      ? "DEPTH · DEEPER DOWN"
      : id === "history"
        ? "HISTORY WINDOW"
        : "LATEST OBSERVATION",
  );
  out += text(
    24,
    simple(o) ? 127 : 114,
    f.value,
    simple(o) ? 54 : 42,
    // No reading in the window: a muted "—" (TOM-665).
    f.value === "—" ? C.muted : C.text,
    "start",
    part("value", 'font-weight="650" data-motion="value"'),
  );
  out += text(368, 111, f.unit, 12, C.muted, "end", part("unit"));
  if (!simple(o))
    out += badge(285, 65, o.window ?? o.historyWindow ?? "24h", C.muted, 87);
  out += historyPlot(data, o, {
    entries,
    x: simple(o) ? 24 : 45,
    y: simple(o) ? 171 : 146,
    w: simple(o) ? 352 : 323,
    h: simple(o) ? 49 : 75,
    depth: id === "depth-graph",
    axes: id !== "sparkline" && !simple(o),
    fill: id === "sparkline" && !simple(o),
  });
  if (id === "depth-graph" && o.referenceLabel)
    out += notice(
      entries[0][1].reference ?? "Depth reference unavailable",
      254,
    );
  return out;
}
function stateHistory(data, o) {
  const entries = entriesFor(data, o).filter(
    ([role]) => !o.lanes?.length || o.lanes.includes(role),
  );
  const all = entries
    .flatMap(([, s]) => s.points)
    .filter((p) => numeric(p.time));
  if (!all.length) return noData("No state history");
  const end = numeric(data.now)
    ? data.now
    : Math.max(...all.map((p) => p.time));
  const start = end - windowMs(o),
    plotX = 112,
    width = 260;
  const hatchId = svgId(o, "state-unknown");
  let out = `<defs><pattern id="${E(hatchId)}" width="6" height="6" patternUnits="userSpaceOnUse"><path d="M0 6 6 0" stroke="var(--quiet)" stroke-width="1"/></pattern></defs>`;
  const rowH = Math.min(50, 158 / entries.length);
  entries.forEach(([role, s], k) => {
    const yy = 76 + k * rowH,
      h = simple(o) ? 12 : 23;
    out += label(24, yy + 11, short(role, 14));
    out += rect(plotX, yy, width, h, C.grid, simple(o) ? 6 : 8);
    s.points.forEach((p, j) => {
      if (!numeric(p.time)) return;
      const next = numeric(s.points[j + 1]?.time) ? s.points[j + 1].time : end;
      if (next <= start || p.time > end) return;
      const unknown =
        p.value === null ||
        p.value === undefined ||
        (p.quality && p.quality !== "good");
      const state = o.states?.[String(p.value)] ?? {
        label: String(p.value),
        tone: "muted",
      };
      const xx = plotX + clamp((p.time - start) / (end - start), 0, 1) * width;
      const ww =
        (Math.max(0, Math.min(end, next) - Math.max(start, p.time)) /
          (end - start)) *
        width;
      out += rect(
        xx,
        yy,
        ww,
        h,
        unknown ? `url(#${E(hatchId)})` : tone(state.tone),
        simple(o) ? 3 : 5,
        'data-motion="interval"',
      );
      if (o.eventMarkers) out += line(xx, yy - 2, xx, yy + h + 2, C.surface, 1);
    });
    const p = s.points.at(-1),
      state = o.states?.[String(p?.value)];
    if (!simple(o))
      out += text(
        plotX,
        yy + h + 14,
        p?.value === null
          ? "Unknown"
          : (state?.label ?? String(p?.value ?? "Unknown")),
        9,
        C.muted,
      );
  });
  out +=
    label(plotX, 244, dateText(start, o)) +
    label(372, 244, dateText(end, o), "end");
  return out + notice("Hatched intervals remain unknown", 254);
}
function comparisonBars(data, o) {
  let items = [...(data.service?.categories ?? data.categories ?? [])].slice(
    0,
    10,
  );
  if (!items.length) return noData("Categories unavailable");
  if (o.sort === "value")
    items.sort(
      (a, b) =>
        (numeric(b.value) ? b.value : -Infinity) -
        (numeric(a.value) ? a.value : -Infinity),
    );
  const values = items.filter((p) => numeric(p.value)).map((p) => p.value);
  if (!values.length) return noData("No measured category totals");
  const min = Math.min(0, ...values),
    max = Math.max(0, ...values),
    span = max - min || 1;
  let out = label(
    24,
    67,
    `${o.period ?? "day"} totals · ${data.service?.unit ?? "unit unavailable"}`,
  );
  if (o.orientation === "horizontal") {
    const rowH = 157 / items.length;
    items.forEach((p, k) => {
      const y = 81 + k * rowH,
        x0 = 125 + (-min / span) * 184;
      out += text(24, y + 10, short(p.label, 17), 10, C.muted);
      out += rect(125, y + 1, 184, simple(o) ? 6 : 10, C.grid, 5);
      if (numeric(p.value)) {
        const xx = 125 + ((p.value - min) / span) * 184;
        out += rect(
          Math.min(x0, xx),
          y + 1,
          Math.abs(xx - x0),
          simple(o) ? 6 : 10,
          p.partial ? C.quiet : C.accent,
          5,
          `${p.partial ? 'stroke="var(--text)" stroke-dasharray="2 3"' : ""} data-motion="bar"`,
        );
      }
      out += text(374, y + 10, fixed(p.value), 11, C.text, "end");
    });
  } else {
    const x = 27,
      y = 97,
      w = 347,
      h = 109,
      y0 = y + (max / span) * h,
      ww = w / items.length;
    if (!simple(o))
      for (let k = 0; k < 3; k++)
        out += line(x, y + (k * h) / 2, x + w, y + (k * h) / 2, C.grid, 0.7);
    out += line(x, y0, x + w, y0, C.quiet, 0.8);
    items.forEach((p, k) => {
      const xx = x + k * ww + 5,
        yy = numeric(p.value) ? y + ((max - p.value) / span) * h : y0;
      if (numeric(p.value))
        out += rect(
          xx,
          Math.min(yy, y0),
          Math.max(5, ww - 13),
          Math.abs(yy - y0) || 1,
          p.partial ? C.quiet : C.accent,
          simple(o) ? 3 : 7,
          `${p.partial ? 'stroke="var(--text)" stroke-dasharray="2 3"' : ""} data-motion="bar"`,
        );
      if (o.labels === "show" || items.length < 7)
        out += text(
          xx + (ww - 13) / 2,
          p.value >= 0 ? yy - 9 : yy + 15,
          fixed(p.value),
          10,
          C.text,
          "middle",
        );
      out += text(
        xx + (ww - 13) / 2,
        228,
        short(p.label, 7),
        9,
        C.muted,
        "middle",
      );
    });
  }
  if (items.some((p) => p.partial))
    out += notice("Partial period marked with a dashed edge", 255);
  return out;
}

function localPoint(position, origin) {
  if (
    !numeric(position?.latitude) ||
    !numeric(position?.longitude) ||
    !numeric(origin?.latitude) ||
    !numeric(origin?.longitude)
  )
    return null;
  return {
    x:
      (((position.longitude - origin.longitude) * Math.PI) / 180) *
      6371000 *
      Math.cos((origin.latitude * Math.PI) / 180),
    y: (((position.latitude - origin.latitude) * Math.PI) / 180) * 6371000,
  };
}
function orientPoint(p, heading) {
  const a = (heading * Math.PI) / 180;
  return {
    x: p.x * Math.cos(a) - p.y * Math.sin(a),
    y: p.x * Math.sin(a) + p.y * Math.cos(a),
  };
}
function vessel(x, y, heading, size = 8, color = C.text) {
  if (!numeric(heading))
    return `<path d="M ${x} ${y - size / 2} L ${x + size / 2} ${y} L ${x} ${y + size / 2} L ${x - size / 2} ${y} Z" fill="${color}" data-motion="position"/>`;
  return `<g transform="translate(${x} ${y}) rotate(${heading})" data-motion="position"><path d="M0 ${-size} Q ${size * 0.35} ${-size * 0.5} ${size * 0.53} ${size * 0.7} L0 ${size * 0.43} L${-size * 0.53} ${size * 0.7} Q${-size * 0.35} ${-size * 0.5} 0 ${-size}Z" fill="${color}" stroke="var(--surface)" stroke-width="1"/></g>`;
}
function radarBase(o, cx, cy, r, name) {
  const clip = svgId(o, `${name}-plot`),
    glow = svgId(o, `${name}-glow`);
  let out = `<defs><clipPath id="${E(clip)}"><circle cx="${cx}" cy="${cy}" r="${r}"/></clipPath><radialGradient id="${E(glow)}"><stop stop-color="var(--accent)" stop-opacity=".06"/><stop offset="1" stop-color="var(--accent)" stop-opacity="0"/></radialGradient></defs>`;
  out += circle(cx, cy, r, simple(o) ? "none" : `url(#${E(glow)})`, C.grid, 1);
  if (!simple(o)) {
    out +=
      circle(cx, cy, r * 0.65, "none", C.grid, 0.6) +
      circle(cx, cy, r * 0.33, "none", C.grid, 0.6);
    for (let a = 0; a < 360; a += 15) {
      const p = point(cx, cy, r + 3, a),
        q = point(cx, cy, r + (a % 90 ? 5 : 9), a);
      out += line(p.x, p.y, q.x, q.y, C.grid, a % 90 ? 0.6 : 1);
    }
  }
  out += line(cx - r, cy, cx + r, cy, C.grid, 0.6, 'stroke-dasharray="2 5"');
  out += line(cx, cy - r, cx, cy + r, C.grid, 0.6, 'stroke-dasharray="2 5"');
  return { out, clip };
}
function layerNote(requested, available, noun) {
  if (!requested?.length) return "";
  const enabled = Array.isArray(available) ? available : [];
  const missing = requested.filter((key) => !enabled.includes(key));
  return missing.length
    ? `${noun} unavailable: ${missing.join(", ")}`
    : `${noun}: ${requested.join(", ")}`;
}
function anchorFace(data, o) {
  const svc = data.service;
  if (!localPoint(svc?.anchorPosition, svc?.anchorPosition))
    return noData("Anchor position unavailable");
  const heading = o.orientation === "heading-up" ? svc.heading : 0;
  if (!numeric(heading)) return noData("Heading-up reference unavailable");
  const cx = simple(o) ? 200 : 224,
    cy = 153,
    r = simple(o) ? 75 : 79;
  const scale = r / o.radius;
  const base = radarBase(o, cx, cy, r, "anchor");
  let out = base.out;
  out += label(
    24,
    69,
    o.orientation === "heading-up" ? "HEADING UP" : "NORTH UP",
  );
  out += text(
    simple(o) ? 372 : 24,
    simple(o) ? 70 : 104,
    `${o.radius} m`,
    simple(o) ? 12 : 27,
    C.text,
    simple(o) ? "end" : "start",
    'font-weight="600"',
  );
  if (!simple(o)) out += label(24, 124, "Display radius");
  const mapped = (p) => {
    const q = localPoint(p, svc.anchorPosition);
    if (!q) return null;
    const v = orientPoint(q, heading);
    return { x: cx + v.x * scale, y: cy - v.y * scale };
  };
  const track = (Array.isArray(svc.track) ? svc.track : []).filter(
    (p) =>
      !numeric(data.now) ||
      (numeric(p.time) && p.time >= data.now - o.trackHours * 3600000),
  );
  let d = "",
    connected = false;
  for (const p of track) {
    const q = mapped(p);
    if (!q) {
      connected = false;
      continue;
    }
    d += `${connected ? " L" : " M"} ${q.x} ${q.y}`;
    connected = true;
  }
  out +=
    `<g clip-path="url(#${E(base.clip)})">` +
    path(d, C.accent, simple(o) ? 1.7 : 2.2, 'data-motion="trace"');
  if (o.showVessels)
    for (const target of svc.targets ?? []) {
      const p = mapped(target);
      if (p)
        out += circle(
          p.x,
          p.y,
          3,
          target.attention ? "var(--attention)" : C.muted,
          "none",
          1,
          'data-motion="position"',
        );
    }
  const q = mapped(svc.position);
  if (q)
    out += vessel(
      q.x,
      q.y,
      numeric(svc.heading) ? svc.heading - heading : null,
      10,
    );
  out += "</g>";
  out += `<path d="M ${cx - 4} ${cy - 5} L ${cx + 4} ${cy - 5} M ${cx} ${cy - 8} V ${cy + 6} M ${cx - 7} ${cy + 1} Q ${cx} ${cy + 13} ${cx + 7} ${cy + 1}" stroke="var(--accent)" stroke-width="1.8" fill="none" stroke-linecap="round"/>`;
  const measured = localPoint(svc.position, svc.anchorPosition);
  if (!simple(o)) {
    out += label(24, 159, "Anchor offset");
    out += text(
      24,
      184,
      measured ? `${fixed(Math.hypot(measured.x, measured.y), 0)} m` : "—",
      22,
      C.text,
    );
  }
  out += notice(
    `Service alarm ${numeric(svc.operationalRadius) ? `${svc.operationalRadius} m` : "unavailable"} · ${o.trackHours}h track`,
    o.weatherLayers?.length ? 238 : 248,
    svc.attention ? "var(--attention)" : C.muted,
  );
  const layers = layerNote(
    o.weatherLayers,
    svc.weatherLayers,
    "Weather layers",
  );
  if (layers) out += label(24, 254, layers);
  return out;
}
function trafficFace(data, o) {
  const svc = data.service;
  if (!svc) return noData("Traffic service unavailable");
  let targets = [...(svc.targets ?? [])].filter(
    (t) => numeric(t.range) && t.range <= o.radius && t.range >= 0,
  );
  targets.sort(
    o.sort === "risk"
      ? (a, b) =>
          (numeric(a.cpa) ? a.cpa : Infinity) -
          (numeric(b.cpa) ? b.cpa : Infinity)
      : (a, b) => a.range - b.range,
  );
  targets = targets.slice(0, o.maxTargets);
  if (!targets.length) return noData("No reported AIS targets");
  if (o.view === "list") {
    const shown = targets.slice(0, 8),
      rowH = 177 / shown.length;
    let out = label(
      24,
      68,
      `${targets.length} reported targets · ${o.radius} m range`,
    );
    shown.forEach((t, k) => {
      const y = 83 + k * rowH,
        color = t.attention ? "var(--attention)" : C.accent;
      if (!simple(o)) out += rect(24, y - 7, 352, rowH - 4, C.surface, 10);
      out += vessel(37, y + 3, numeric(t.course) ? t.course : null, 6, color);
      out += text(
        52,
        y + 6,
        short(o.labelFields?.includes("name") ? (t.name ?? t.id) : t.id, 27),
        11,
        C.text,
      );
      const details = (o.labelFields ?? [])
        .filter((field) => field !== "name")
        .map((field) => {
          if (field === "range") return `${fixed(t.range, 0)} m`;
          if (field === "bearing")
            return numeric(t.bearing)
              ? `${fixed(t.bearing, 0)}°`
              : "Bearing unavailable";
          if (field === "CPA" || field === "cpa")
            return numeric(t.cpa)
              ? `CPA ${fixed(t.cpa, 0)} m`
              : "CPA unavailable";
          return t[field] ?? "—";
        })
        .join(" · ");
      out += text(372, y + 6, short(details, 22), 10, C.muted, "end");
    });
    return out;
  }
  const cx = simple(o) ? 200 : 191,
    cy = 154,
    r = 77;
  const base = radarBase(o, cx, cy, r, "traffic");
  let out =
    base.out +
    label(24, 67, "NORTH UP · REPORTED BEARINGS") +
    label(372, 67, `${o.radius} m`, "end");
  out += vessel(cx, cy, numeric(svc.heading) ? svc.heading : null, 10);
  for (const [k, t] of targets.entries()) {
    if (!numeric(t.bearing)) continue;
    const p = point(cx, cy, clamp(t.range / o.radius, 0, 1) * r, t.bearing);
    const color = t.attention ? "var(--attention)" : C.accent;
    if (!simple(o))
      out += circle(p.x, p.y, 9, "none", color, 0.8, 'opacity=".3"');
    out += vessel(p.x, p.y, numeric(t.course) ? t.course : null, 6, color);
    out += text(
      p.x + 10,
      p.y + 3,
      short(
        o.labelFields?.includes("name") ? (t.name ?? t.id) : t.id,
        simple(o) ? 10 : 12,
      ),
      9,
      C.muted,
    );
    if (!simple(o) && k < 3) {
      const x = 304,
        y = 113 + k * 41;
      out += text(x, y, short(t.name ?? t.id, 12), 10, C.text);
      out += text(x, y + 15, `${fixed(t.range, 0)} m`, 11, C.muted);
    }
  }
  out += notice(
    `${targets.length} reported targets${targets.some((t) => !numeric(t.bearing)) ? " · bearing missing for some" : ""}`,
    248,
  );
  return out;
}
function routeFace(data, o) {
  const svc = data.service,
    waypoints = svc?.waypoints ?? [];
  if (!waypoints.length) return noData("Active route unavailable");
  if (o.view === "leg-list") {
    const shown = waypoints.slice(0, 8),
      rowH = 174 / shown.length;
    let out = label(24, 68, svc.activeLeg ?? "ACTIVE ROUTE");
    shown.forEach((p, k) => {
      const y = 83 + k * rowH;
      out += circle(
        34,
        y + 1,
        8,
        k === 0 ? C.accent : C.surface,
        k === 0 ? "none" : C.grid,
        1,
      );
      out += text(
        34,
        y + 4,
        k + 1,
        9,
        k === 0 ? "var(--bg)" : C.muted,
        "middle",
      );
      out += text(
        51,
        y + 5,
        short(p.name ?? `Waypoint ${k + 1}`, 25),
        11,
        C.text,
      );
      const facts = (o.fields ?? []).map((field) => {
        if (field === "distance")
          return numeric(p.distance) ? `${fixed(p.distance, 0)} m` : "— m";
        if (field === "bearing")
          return numeric(p.bearing) ? `${fixed(p.bearing, 0)}°` : "—°";
        if (field.toUpperCase() === "ETA") return p.eta ?? "ETA unavailable";
        return p[field] ?? "—";
      });
      out += text(373, y + 5, short(facts.join(" · "), 30), 9, C.muted, "end");
      if (!simple(o))
        out += line(51, y + rowH - 9, 374, y + rowH - 9, C.grid, 0.7);
    });
    return out;
  }
  const origin = svc.position ?? waypoints[0];
  const heading = o.orientation === "heading-up" ? svc.heading : 0;
  if (!numeric(heading)) return noData("Heading-up reference unavailable");
  const mapped = waypoints
    .map((p) => ({ waypoint: p, local: localPoint(p, origin) }))
    .filter((p) => p.local);
  if (!mapped.length) return noData("Waypoint coordinates unavailable");
  const rotated = mapped.map((p) => ({
    ...p,
    local: orientPoint(p.local, heading),
  }));
  const max = Math.max(
    100,
    ...rotated.flatMap((p) => [Math.abs(p.local.x), Math.abs(p.local.y)]),
  );
  const cx = 200,
    cy = 155,
    scale = 76 / max;
  const base = radarBase(o, cx, cy, 78, "route");
  let out =
    base.out +
    label(24, 67, o.orientation === "heading-up" ? "HEADING UP" : "NORTH UP");
  const d = rotated
    .map(
      (p, k) =>
        `${k ? "L" : "M"} ${cx + p.local.x * scale} ${cy - p.local.y * scale}`,
    )
    .join(" ");
  if (!simple(o)) out += path(d, C.accent, 8, 'opacity=".08"');
  out += path(d, C.accent, simple(o) ? 2 : 2.8, 'data-motion="route"');
  if (o.showTrack && Array.isArray(svc.track)) {
    let td = "",
      connected = false;
    for (const p of svc.track) {
      const q = localPoint(p, origin);
      if (!q) {
        connected = false;
        continue;
      }
      const v = orientPoint(q, heading);
      td += `${connected ? "L" : "M"} ${cx + v.x * scale} ${cy - v.y * scale} `;
      connected = true;
    }
    out +=
      `<g clip-path="url(#${E(base.clip)})">` +
      path(td, C.quiet, 1.4, 'stroke-dasharray="3 4" data-motion="trace"') +
      "</g>";
  }
  rotated.forEach((p, k) => {
    const x = cx + p.local.x * scale,
      y = cy - p.local.y * scale;
    out += circle(
      x,
      y,
      simple(o) ? 3 : 5,
      C.accent,
      C.surface,
      1,
      'data-motion="waypoint"',
    );
    out += text(
      clamp(x + 9, 32, 310),
      clamp(y - 7, 91, 230),
      short(p.waypoint.name ?? String(k + 1), 15),
      9,
      C.muted,
    );
  });
  out += vessel(
    cx,
    cy,
    numeric(svc.heading) ? svc.heading - heading : null,
    10,
  );
  out += notice(
    svc.activeLeg ?? "Active route",
    o.mapLayers?.length ? 238 : 247,
  );
  const layers = layerNote(o.mapLayers, svc.mapLayers, "Map layers");
  if (layers) out += label(24, 254, layers);
  return out;
}
function serviceRows(
  data,
  o,
  fields,
  { x = 24, y = 86, w = 352, h = 148, max = 8, cells = false } = {},
) {
  const chosen = (
    fields?.length
      ? fields
      : Object.keys(data.service?.values ?? data.values ?? {})
  ).slice(0, max);
  if (!chosen.length) return noData("Service data unavailable");
  const rowH = h / chosen.length;
  return chosen
    .map((field, k) => {
      const yy = y + k * rowH;
      return (
        (cells ? rect(x, yy - 12, w, rowH - 4, C.surface, 10) : "") +
        text(
          x + (cells ? 11 : 0),
          yy + 2,
          short(fieldLabel(data, field), 25),
          11,
          C.muted,
        ) +
        text(
          x + w - (cells ? 11 : 0),
          yy + 2,
          fieldText(data, field, o),
          chosen.length > 6 ? 11 : 15,
          C.text,
          "end",
          'font-weight="550" data-motion="value"',
        ) +
        (!cells && !simple(o)
          ? line(x, yy + rowH - 12, x + w, yy + rowH - 12, C.grid, 0.6)
          : "")
      );
    })
    .join("");
}
function gpsFace(data, o) {
  const svc = data.service;
  if (!svc) return noData("GNSS service unavailable");
  const fields = o.fields?.length ? o.fields : Object.keys(svc.values ?? {});
  const satellites = svc.satellites ?? [];
  let out = o.receiverLabel ? label(24, 68, svc.name ?? "GNSS receiver") : "";
  if (o.satelliteView === "none")
    return (
      out + serviceRows(data, o, fields, { y: 99, h: 153, cells: !simple(o) })
    );
  const rowH = Math.min(37, 162 / Math.max(1, fields.length));
  fields.slice(0, 8).forEach((field, k) => {
    const y = 90 + k * rowH;
    out += label(24, y, fieldLabel(data, field));
    out += text(
      24,
      y + (rowH > 25 ? 22 : 15),
      fieldText(data, field, o),
      rowH > 25 ? 19 : 12,
      C.text,
      "start",
      'font-weight="550" data-motion="value"',
    );
  });
  if (o.satelliteView === "sky") {
    const cx = 274,
      cy = 160,
      r = simple(o) ? 59 : 68;
    out += circle(cx, cy, r) + circle(cx, cy, r * 0.5, "none", C.grid, 0.8);
    if (!simple(o))
      out +=
        line(cx - r, cy, cx + r, cy) +
        line(cx, cy - r, cx, cy + r) +
        text(cx, cy - r - 9, "N", 9, C.muted, "middle");
    for (const sat of satellites) {
      if (!numeric(sat.azimuth) || !numeric(sat.elevation)) continue;
      const p = point(
        cx,
        cy,
        r * (1 - clamp(sat.elevation, 0, 90) / 90),
        sat.azimuth,
      );
      out += circle(
        p.x,
        p.y,
        simple(o) ? 3 : 4,
        C.accent,
        "none",
        1,
        'data-motion="satellite"',
      );
      if (!simple(o) && sat.id)
        out += text(p.x + 6, p.y - 6, short(sat.id, 6), 8, C.muted);
    }
  } else {
    const chosen = satellites.slice(0, 12),
      ww = 179 / Math.max(1, chosen.length),
      x = 190,
      base = 225;
    out += line(x, base, 372, base, C.grid, 0.8);
    chosen.forEach((sat, k) => {
      const h = numeric(sat.strength) ? clamp(sat.strength, 0, 60) * 1.7 : 0;
      out += rect(
        x + k * ww + 2,
        base - h,
        Math.max(4, ww - 5),
        h,
        C.accent,
        simple(o) ? 3 : 5,
        'data-motion="satellite-strength"',
      );
      if (!simple(o))
        out += text(
          x + k * ww + ww / 2,
          239,
          sat.id ?? k + 1,
          7,
          C.quiet,
          "middle",
        );
    });
    out += label(190, 103, "REPORTED SIGNAL STRENGTH");
  }
  if (!satellites.length)
    out += label(206, 164, "Satellite detail unavailable");
  return out;
}
function signalFace(data, o) {
  const r = sample(data, "signal"),
    n = numberValue(data, "signal");
  if (n === null) return noData("Connection quality unavailable");
  const min = o.scale?.[0],
    max = o.scale?.[1];
  if (!numeric(min) || !numeric(max) || max <= min)
    return noData("Signal scale unavailable");
  const fraction = clamp((n - min) / (max - min), 0, 1);
  let out = o.linkLabel
    ? label(24, 68, data.service?.name ?? r.sourceLabel ?? "Link")
    : "";
  if (o.display !== "number") {
    const x = o.display === "both" ? 217 : 103,
      base = o.history ? 202 : 222;
    for (let k = 0; k < 5; k++) {
      const h = 29 + k * 21,
        active = fraction > k / 5;
      out += rect(
        x + k * 28,
        base - h,
        17,
        h,
        active ? C.accent : C.grid,
        simple(o) ? 5 : 8,
        'data-motion="signal-bar"',
      );
      if (!simple(o) && active)
        out += rect(x + k * 28, base - h, 17, 8, C.text, 4, 'opacity=".16"');
    }
  }
  if (o.display !== "bars")
    out += metric(
      data,
      "signal",
      o,
      o.display === "both" ? 24 : 200,
      simple(o) ? 160 : 153,
      53,
      o.display === "both" ? "start" : "middle",
    );
  if (!simple(o))
    out += label(24, 234, `Display scale ${min} to ${max} ${r.unit ?? ""}`);
  if (o.history)
    out += historyPlot(data, o, {
      x: 24,
      y: 229,
      w: 352,
      h: 20,
      axes: false,
      timeline: false,
      legend: false,
    });
  return out;
}
function boardHealthFace(data, o) {
  const members = (data.service?.members ?? [])
    .filter((m) => !o.include?.length || o.include.includes(m.id))
    .filter((m) => o.modeScope !== "current" || m.inCurrentMode !== false);
  if (!members.length) return noData("No declared board members");
  const order = { disconnected: 0, missing: 1, stale: 2, good: 3 };
  members.sort((a, b) => (order[a.quality] ?? 1) - (order[b.quality] ?? 1));
  const good = members.filter((m) => m.quality === "good").length;
  let out = label(24, 69, "REPORTED DATA QUALITY");
  out += text(
    24,
    110,
    `${good}/${members.length}`,
    38,
    C.text,
    "start",
    'font-weight="650" data-motion="value"',
  );
  out += label(155, 108, "current sources");
  const shown = members.slice(0, 6),
    rowH = 116 / shown.length;
  shown.forEach((m, k) => {
    const y = 136 + k * rowH;
    if (!simple(o)) out += rect(24, y - 12, 352, rowH - 3, C.surface, 8);
    const color = m.quality === "good" ? "var(--good)" : C.quiet;
    out += circle(36, y - 1, 3.5, color, "none");
    out += text(49, y + 3, short(m.name ?? m.id, 30), 11, C.text);
    out += text(365, y + 3, m.quality ?? "unknown", 10, color, "end");
  });
  return out;
}
function forecastFace(data, o) {
  const svc = data.service;
  const periods = (svc?.periods ?? [])
    .filter(
      (p) =>
        numeric(p.time) &&
        (!numeric(data.now) || p.time <= data.now + o.hours * 3600000),
    )
    .slice(0, o.layout === "summary" ? 1 : 5);
  if (!periods.length) return noData("Forecast source unavailable");
  const fields = (o.fields ?? []).slice(0, 8),
    ww = 352 / periods.length;
  let out = label(24, 68, `${o.hours}h FORECAST`);
  periods.forEach((p, k) => {
    const x = 24 + k * ww,
      body = { values: p.values ?? {} };
    if (!simple(o)) out += rect(x, 82, ww - 5, 149, C.surface, 13);
    const middle = x + (ww - 5) / 2;
    out += text(middle, 101, dateText(p.time, o), 10, C.muted, "middle");
    const rowH = Math.min(30, 100 / Math.max(1, fields.length));
    fields.forEach((field, j) => {
      const y = 123 + j * rowH;
      out += text(
        middle,
        y,
        fieldText(body, field, o),
        fields.length > 4 ? 10 : j === 0 ? 18 : 13,
        j === 0 ? C.text : C.muted,
        "middle",
        j === 0 ? 'font-weight="600" data-motion="forecast"' : "",
      );
      if (periods.length === 1)
        out += label(x + 12, y, fieldLabel(body, field));
    });
    if (o.showSeaState)
      out += text(
        middle,
        223,
        `Sea ${p.seaState ?? "—"}`,
        9,
        C.muted,
        "middle",
      );
  });
  if (o.showIssuedAt)
    out += notice(
      `Issued ${dateText(svc.issuedAt, o)} · ${svc.source ?? "Source unavailable"}`,
      252,
    );
  return out;
}
function tideFace(data, o) {
  const entries = entriesFor(data, o);
  if (!entries.length) return noData("Tide history unavailable");
  const latest = latestHistory(data, o),
    current = latest?.sample;
  const f = current
    ? reading({ values: { value: current } }, "value", o)
    : { value: "—", unit: "" };
  let out = label(24, 70, "RELATIVE TIDE HEIGHT");
  out +=
    text(
      24,
      119,
      f.value,
      simple(o) ? 52 : 42,
      C.text,
      "start",
      'font-weight="650" data-motion="value"',
    ) + text(368, 115, f.unit, 13, C.muted, "end");
  const graphOptions = { ...o, window: `${o.lookbackHours}h`, legend: "hide" };
  out += historyPlot(data, graphOptions, {
    entries,
    x: simple(o) ? 24 : 45,
    y: simple(o) ? 167 : 151,
    w: simple(o) ? 352 : 323,
    h: simple(o) ? 55 : 73,
    axes: !simple(o),
    fill: !simple(o),
    tide: true,
    timeline: false,
  });
  const svc = data.service ?? {};
  if (o.datumLabel || o.stationLabel)
    out += notice(
      [
        o.datumLabel ? (svc.datum ?? "Datum unavailable") : "",
        o.stationLabel ? (svc.station ?? "Station unavailable") : "",
      ]
        .filter(Boolean)
        .join(" · "),
      252,
    );
  return out;
}
function moonFace(data, o) {
  const svc = data.service ?? {},
    phase = svc.phase;
  if (!numeric(phase)) return noData("Moon phase source unavailable");
  if (
    o.hemisphere === "source-derived" &&
    !["north", "south"].includes(svc.hemisphere)
  )
    return noData("Moon hemisphere reference unavailable");
  const p = ((phase % 1) + 1) % 1,
    illumination = (1 - Math.cos(p * Math.PI * 2)) / 2;
  const south =
    o.hemisphere === "south" ||
    (o.hemisphere === "source-derived" && svc.hemisphere === "south");
  const waxing = p < 0.5,
    side = (south ? !waxing : waxing) ? 1 : -1;
  const cx = o.display === "both" && !simple(o) ? 130 : 200,
    cy = 153,
    r = simple(o) ? 48 : 56;
  let out = o.labels
    ? label(24, 68, svc.phaseName ?? (waxing ? "Waxing" : "Waning"))
    : "";
  if (o.display !== "illumination") {
    if (!simple(o)) out += circle(cx, cy, r + 9, "none", C.grid, 0.7);
    out += circle(cx, cy, r, C.grid, "none");
    const rx = Math.max(0.01, Math.abs(Math.cos(p * Math.PI * 2)) * r);
    out += `<path d="M ${cx} ${cy - r} A ${r} ${r} 0 0 ${side > 0 ? 1 : 0} ${cx} ${cy + r} A ${rx} ${r} 0 0 ${illumination > 0.5 ? (side > 0 ? 1 : 0) : side > 0 ? 0 : 1} ${cx} ${cy - r} Z" fill="var(--text)" data-motion="phase"/>`;
    if (!simple(o))
      out +=
        circle(cx - 9, cy + 14, 4, C.surface, "none", 1, 'opacity=".15"') +
        circle(cx + 15, cy - 10, 7, C.surface, "none", 1, 'opacity=".1"');
  }
  if (o.display !== "phase") {
    const x = o.display === "both" && !simple(o) ? 254 : 200,
      y = o.display === "both" ? (simple(o) ? 217 : 146) : 156;
    out += text(
      x,
      y,
      `${Math.round(illumination * 100)}%`,
      o.display === "illumination" ? 58 : simple(o) ? 24 : 39,
      C.text,
      "middle",
      'font-weight="650" data-motion="value"',
    );
    out += text(x, y + 21, "illuminated", 11, C.muted, "middle");
  }
  if (o.riseSet)
    out += notice(
      `Rise ${svc.rise ?? "unavailable"} · Set ${svc.set ?? "unavailable"}`,
      254,
    );
  return out;
}
function pressureFace(data, o) {
  const r = sample(data, "pressure");
  if (!r) return noData("Pressure source unavailable");
  const options = { ...o, unit: o.units ?? o.unit };
  let out = label(24, 70, "BAROMETRIC PRESSURE");
  out += metric(
    data,
    "pressure",
    options,
    24,
    simple(o) ? 136 : 124,
    simple(o) ? 58 : 50,
  );
  const series = prepareSeries(
      data,
      options,
      entriesFor(data, o).slice(0, 1),
    )[0]?.[1],
    pts = series?.points?.filter(usable) ?? [];
  if (pts.length > 1) {
    const selectedUnit = reading(data, "pressure", options).unit;
    const delta =
      numeric(pts.at(-1).value) && numeric(pts[0].value)
        ? pts.at(-1).value - pts[0].value
        : null;
    const converted =
      numeric(delta) &&
      series.unit &&
      selectedUnit &&
      dimensionForUnit(series.unit) === dimensionForUnit(selectedUnit)
        ? convert(delta, series.unit, selectedUnit) -
          convert(0, series.unit, selectedUnit)
        : delta;
    out += text(
      374,
      121,
      `${converted >= 0 ? "+" : ""}${fixed(converted)}`,
      25,
      C.accent,
      "end",
      'data-motion="delta"',
    );
    out += label(374, 143, `Measured Δ ${selectedUnit}`, "end");
  } else out += label(374, 125, "Tendency unavailable", "end");
  if (o.showHistory)
    out += historyPlot(data, options, {
      x: 24,
      y: simple(o) ? 194 : 184,
      w: 352,
      h: simple(o) ? 34 : 48,
      axes: false,
      fill: !simple(o),
      timeline: false,
      legend: false,
    });
  return out;
}

function powerFace(data, o) {
  const svc = data.service ?? {},
    soc = sample(data, "soc");
  if (!soc) return noData("House bank telemetry unavailable");
  const fields = o.fields ?? [],
    displayed = fields.includes("soc") ? fields : ["soc", ...fields];
  const sf = reading(data, "soc", o);
  let out = label(24, 70, "STATE OF CHARGE");
  out += text(
    24,
    simple(o) ? 137 : 124,
    sf.value,
    simple(o) ? 62 : 54,
    C.text,
    "start",
    'font-weight="650" data-motion="value"',
  );
  out += text(24, simple(o) ? 159 : 148, sf.unit, 12, C.muted);
  const current = numberValue(data, "current");
  if (displayed.includes("current")) {
    out += label(
      374,
      69,
      current === null
        ? "Current unavailable"
        : current < 0
          ? "Discharging"
          : current > 0
            ? "Charging"
            : "No net current",
      "end",
    );
    out += text(
      374,
      110,
      fieldText(data, "current", o),
      simple(o) ? 22 : 28,
      C.text,
      "end",
      'font-weight="550" data-motion="value"',
    );
    if (sample(data, "power") && !simple(o))
      out += text(374, 133, fieldText(data, "power", o), 11, C.muted, "end");
  }
  if (numeric(soc.value)) {
    const pct = clamp(soc.value / 100, 0, 1),
      yy = o.history ? (simple(o) ? 171 : 159) : 177;
    out +=
      rect(24, yy, 352, simple(o) ? 5 : 10, C.grid, 5) +
      rect(
        24,
        yy,
        352 * pct,
        simple(o) ? 5 : 10,
        svc.attention ? "var(--attention)" : C.accent,
        5,
        'data-motion="charge"',
      );
    if (!simple(o))
      out +=
        line(
          24 + (352 * o.concernSoc) / 100,
          yy - 4,
          24 + (352 * o.concernSoc) / 100,
          yy + 14,
          C.muted,
          1,
        ) + label(374, 187, `Visual guide ${o.concernSoc}%`, "end");
  }
  if (o.history)
    out += historyPlot(
      data,
      { ...o, legend: "hide", extremes: "off", envelope: !simple(o) },
      {
        entries: entriesFor(data, o).slice(0, 1),
        x: 24,
        y: simple(o) ? 185 : 189,
        w: 352,
        h: 25,
        axes: false,
        timeline: false,
        legend: false,
      },
    );
  const facts = displayed.filter(
    (field) => !["soc", "current"].includes(field),
  );
  if (sample(data, "autonomy") && !facts.includes("autonomy"))
    facts.unshift("autonomy");
  const columns = Math.max(1, Math.ceil(facts.length / 2));
  facts.slice(0, 8).forEach((field, k) => {
    const x = 24 + ((k % columns) * 352) / columns,
      y = 227 + Math.floor(k / columns) * 14;
    out += text(
      x,
      y,
      short(
        `${fieldLabel(data, field)} ${fieldText(data, field, o)}`,
        columns > 3 ? 16 : 26,
      ),
      9,
      C.muted,
    );
  });
  if (estimateNote(data)) out += label(24, 254, estimateNote(data));
  return out;
}
function engineFace(data, o) {
  const engines = (data.service?.engines ?? []).filter(
    (e) => !o.engineIds?.length || o.engineIds.includes(e.id),
  );
  if (!engines.length) return noData("Engine identities unavailable");
  const fields = (o.probeKeys?.length ? o.probeKeys : (o.fields ?? [])).slice(
    0,
    8,
  );
  const chosen = engines.slice(0, 4),
    cols = o.arrangement === "paired" ? Math.min(2, chosen.length) : 1;
  const rows = Math.ceil(chosen.length / cols),
    height = 188 / rows,
    width = 352 / cols;
  let out = "";
  chosen.forEach((engine, k) => {
    const x = 24 + (k % cols) * width,
      y = 57 + Math.floor(k / cols) * height;
    const body = { values: engine.values ?? {} },
      ww = width - 7;
    if (!simple(o)) out += rect(x, y, ww, height - 6, C.surface, 15);
    out += text(
      x + 10,
      y + 20,
      short(engine.name ?? engine.id, cols === 2 ? 20 : 35),
      11,
      C.accent,
    );
    const dense = height < 125;
    if (dense) {
      const factCols =
        cols === 1
          ? Math.min(4, fields.length || 1)
          : Math.min(2, fields.length || 1);
      const factRows = Math.ceil(fields.length / factCols),
        rh = Math.max(10, (height - 30) / factRows);
      fields.forEach((field, j) => {
        const xx = x + 10 + ((j % factCols) * (ww - 20)) / factCols,
          yy = y + 39 + Math.floor(j / factCols) * rh;
        out += text(
          xx,
          yy,
          short(
            `${fieldLabel(body, field)} ${fieldText(body, field, o)}`,
            factCols > 2 ? 17 : 23,
          ),
          9,
          C.text,
          "start",
          'data-motion="engine-reading"',
        );
      });
    } else {
      const primary = fields[0];
      if (primary) {
        out += text(
          x + 10,
          y + 66,
          reading(body, primary, o).value,
          simple(o) ? 40 : 34,
          C.text,
          "start",
          'font-weight="650" data-motion="engine-reading"',
        );
        out += label(
          x + 10,
          y + 87,
          `${fieldLabel(body, primary)} · ${reading(body, primary, o).unit}`,
        );
      }
      const rest = fields.slice(1),
        factCols = rest.length > 4 ? 2 : 1,
        factRows = Math.ceil(rest.length / factCols),
        rh = Math.min(25, 83 / Math.max(1, factRows));
      rest.forEach((field, j) => {
        const xx = x + 10 + ((j % factCols) * (ww - 20)) / factCols,
          yy = y + 112 + Math.floor(j / factCols) * rh;
        out += text(
          xx,
          yy,
          short(fieldLabel(body, field), factCols === 2 ? 12 : 26),
          9,
          C.muted,
        );
        out += text(
          xx,
          yy + 13,
          fieldText(body, field, o),
          factCols === 2 ? 11 : 14,
          C.text,
          "start",
          'data-motion="engine-reading"',
        );
      });
    }
    if (engine.attention)
      out += circle(x + ww - 12, y + 17, 3, "var(--attention)", "none");
  });
  return out;
}
function tanksFace(data, o) {
  const svc = data.service ?? {};
  let tanks = (svc.tanks ?? []).filter(
    (t) => !o.prominentTanks?.length || o.prominentTanks.includes(t.id),
  );
  if (o.sort === "name")
    tanks.sort((a, b) =>
      String(a.name ?? a.id).localeCompare(String(b.name ?? b.id)),
    );
  if (o.sort === "remaining")
    tanks.sort(
      (a, b) =>
        (numeric(a.level) ? a.level : Infinity) -
        (numeric(b.level) ? b.level : Infinity),
    );
  tanks = tanks.slice(0, 8);
  if (!tanks.length) return noData("Tank models unavailable");
  const rowH = Math.min(53, 177 / tanks.length);
  let out = "";
  tanks.forEach((t, k) => {
    const y = 70 + k * rowH,
      color = t.attention ? "var(--attention)" : C.accent;
    out += text(
      24,
      y,
      short(t.name ?? t.id, 29),
      tanks.length > 5 ? 10 : 12,
      C.text,
    );
    const percent = numeric(t.level) ? `${fixed(t.level, 0)}%` : "—%";
    const quantity = `${numeric(t.quantity) ? fixed(t.quantity, 0) : "—"} ${t.unit ?? ""}`;
    const f =
      o.display === "percent"
        ? percent
        : o.display === "quantity"
          ? quantity
          : `${percent} · ${quantity}`;
    out += text(
      374,
      y,
      f,
      tanks.length > 5 ? 10 : 13,
      t.attention ? color : C.text,
      "end",
      'font-weight="550" data-motion="tank-value"',
    );
    const yy = y + (rowH < 27 ? 5 : 11),
      h = simple(o) ? 5 : rowH < 27 ? 5 : 9;
    out += rect(24, yy, 352, h, C.grid, h / 2);
    if (numeric(t.level))
      out += rect(
        24,
        yy,
        352 * clamp(t.level / 100, 0, 1),
        h,
        color,
        h / 2,
        'data-motion="tank-fill"',
      );
    if (!simple(o) && rowH >= 27)
      for (let j = 1; j < 4; j++)
        out += line(
          24 + (352 * j) / 4,
          yy - 1,
          24 + (352 * j) / 4,
          yy + h + 1,
          C.surface,
          2,
        );
  });
  if (o.forecast) out += notice(svc.forecast ?? "Forecast unavailable", 252);
  return out;
}
function waterFace(data, o) {
  const svc = data.service ?? {},
    fields = o.fields ?? [];
  if (!fields.length || !Object.keys(svc.values ?? {}).length)
    return noData("Water budget unavailable");
  const opts = { ...o, unit: o.units ?? o.unit },
    primary = fields[0];
  let out = label(24, 69, fieldLabel(data, primary));
  out += metric(
    data,
    primary,
    opts,
    24,
    simple(o) ? 134 : 124,
    simple(o) ? 58 : 49,
  );
  out += badge(285, 68, o.horizon ?? "day", C.muted, 89);
  const rest = fields.slice(1);
  if (!simple(o) && rest.length <= 3) {
    out += `<path d="M 159 161 C 175 161 175 190 193 190 L 230 190 M 230 190 C 250 190 250 161 267 161" stroke="var(--grid)" stroke-width="2" fill="none"/>`;
    out +=
      rect(178, 173, 47, 36, C.surface, 11) +
      path(
        "M 183 195 Q194 188 204 195 Q214 202 220 195",
        C.accent,
        2,
        'data-motion="water-flow"',
      );
  }
  const rows = rest.length > 4 ? 2 : 1,
    cols = Math.max(1, Math.ceil(rest.length / rows));
  rest.forEach((field, k) => {
    const x = 24 + ((k % cols) * 352) / cols,
      y = 181 + Math.floor(k / cols) * 33;
    out += text(
      x,
      y,
      short(fieldLabel(data, field), cols > 3 ? 13 : 24),
      10,
      C.muted,
    );
    out += text(
      x,
      y + 20,
      fieldText(data, field, opts),
      cols > 3 ? 12 : 19,
      C.text,
      "start",
      'data-motion="water-reading"',
    );
  });
  const estimates = estimateNote(data);
  out += notice(
    estimates ||
      `${o.horizon} · ${svc.confidence ?? "Estimate context unavailable"}`,
    252,
  );
  return out;
}
function depthSafetyFace(data, o) {
  const svc = data.service ?? {},
    d = sample(data, "depth");
  if (!d) return noData("Depth and safety model unavailable");
  let out = label(24, 69, "REPORTED DEPTH");
  out += metric(
    data,
    "depth",
    o,
    24,
    simple(o) ? 138 : 128,
    simple(o) ? 58 : 50,
  );
  out += label(24, 173, d.reference ?? "Depth reference unavailable");
  const color = svc.attention ? "var(--attention)" : C.muted;
  out += text(
    374,
    93,
    "SERVICE THRESHOLD",
    9,
    C.muted,
    "end",
    'letter-spacing=".5"',
  );
  out += text(
    374,
    118,
    numeric(svc.threshold)
      ? `${fixed(svc.threshold)} ${d.unit ?? ""}`
      : "Unavailable",
    22,
    color,
    "end",
    'data-motion="threshold"',
  );
  if (!simple(o)) {
    out += path(
      "M 259 151 Q 270 142 283 151 Q 296 160 309 151 Q 322 142 335 151 Q 348 160 360 151",
      C.grid,
      1.5,
    );
    out += line(307, 151, 307, 184, C.accent, 1.5, 'stroke-dasharray="2 4"');
    out += path("M300 177 307 184 314 177", C.accent, 1.8);
    out += label(
      374,
      177,
      svc.attention ? "Host attention active" : "Host-owned safety limit",
      "end",
    );
  }
  if (o.trend === "short")
    out += historyPlot(
      data,
      {
        ...o,
        scaleMode: o.visualScale === "fixed" ? "fixed" : "auto",
        legend: "hide",
        extremes: "off",
      },
      {
        entries: entriesFor(data, o).slice(0, 1),
        x: 24,
        y: 206,
        w: 352,
        h: 35,
        axes: false,
        timeline: false,
        legend: false,
        fill: !simple(o),
      },
    );
  return out;
}
function environmentFace(data, o) {
  const fields = o.order?.length
    ? [...o.order, ...(o.fields ?? []).filter((f) => !o.order.includes(f))]
    : (o.fields ?? []);
  if (!fields.length) return noData("Environment bindings unavailable");
  const opts = { ...o, unit: o.units ?? o.unit },
    chosen = fields.slice(0, 8);
  let out = "";
  const primary = chosen.slice(0, 2),
    width = 176;
  primary.forEach((field, k) => {
    const x = 24 + k * width;
    out += label(x, 72, fieldLabel(data, field));
    out += text(
      x,
      simple(o) ? 128 : 122,
      reading(data, field, opts).value,
      simple(o) ? 48 : 40,
      C.text,
      "start",
      'font-weight="650" data-motion="environment-reading"',
    );
    out += label(x, 148, reading(data, field, opts).unit);
    if (!simple(o))
      out += label(
        x,
        168,
        sample(data, field)?.sourceLabel ??
          data.service?.source ??
          "Source unavailable",
      );
  });
  const first = entriesFor(data, o).slice(0, 1);
  if (first.length)
    out += historyPlot(
      data,
      { ...opts, legend: "hide", extremes: "off" },
      {
        entries: first,
        x: 24,
        y: simple(o) ? 176 : 186,
        w: 352,
        h: simple(o) ? 29 : 26,
        axes: false,
        timeline: false,
        legend: false,
        fill: !simple(o),
      },
    );
  const rest = chosen.slice(2),
    columns = Math.min(3, Math.max(1, rest.length));
  rest.forEach((field, k) => {
    const x = 24 + ((k % columns) * 352) / columns,
      y = 232 + Math.floor(k / columns) * 17;
    out += text(
      x,
      y,
      short(
        `${fieldLabel(data, field)} ${fieldText(data, field, opts)}`,
        columns === 3 ? 20 : 31,
      ),
      9,
      C.muted,
      "start",
      'data-motion="environment-reading"',
    );
  });
  if (simple(o))
    out += label(
      24,
      160,
      data.service?.source ??
        sample(data, primary[0])?.sourceLabel ??
        "Source unavailable",
    );
  return out;
}
function changesFace(data, o) {
  const svc = data.service ?? {};
  let changes = (svc.changes ?? []).filter(
    (c) =>
      (!o.fields?.length || o.fields.includes(c.field)) &&
      (!numeric(data.now) ||
        (numeric(c.time) && c.time >= data.now - o.lookbackMinutes * 60000)),
  );
  changes.sort(
    o.sort === "magnitude"
      ? (a, b) =>
          (numeric(b.delta) ? Math.abs(b.delta) : -Infinity) -
          (numeric(a.delta) ? Math.abs(a.delta) : -Infinity)
      : (a, b) =>
          (numeric(b.time) ? b.time : 0) - (numeric(a.time) ? a.time : 0),
  );
  if (!changes.length)
    return noData(
      svc.available
        ? "No measured recent changes"
        : "Change service unavailable",
    );
  changes = changes.slice(0, Math.min(o.maxRows ?? 5, 8));
  const rowH = 172 / changes.length;
  const magnitudes = new Map();
  for (const c of changes)
    if (numeric(c.delta))
      magnitudes.set(
        c.unit ?? "",
        Math.max(magnitudes.get(c.unit ?? "") ?? 1, Math.abs(c.delta)),
      );
  let out = label(24, 69, `MEASURED CHANGES · ${o.lookbackMinutes} MIN`);
  changes.forEach((c, k) => {
    const y = 91 + k * rowH;
    if (!simple(o)) out += rect(24, y - 14, 352, rowH - 5, C.surface, 12);
    out += text(34, y + 2, short(c.label ?? c.field, 26), 11, C.text);
    const delta = numeric(c.delta)
      ? `${c.delta >= 0 ? "+" : "−"}${fixed(Math.abs(c.delta))} ${c.unit ?? ""}`
      : "Delta unavailable";
    out += text(
      365,
      y + 3,
      delta,
      changes.length > 5 ? 12 : 19,
      C.accent,
      "end",
      'font-weight="550" data-motion="delta"',
    );
    if (rowH > 31) {
      const age =
        numeric(c.time) && numeric(data.now)
          ? `${Math.max(0, Math.floor((data.now - c.time) / 60000))}m ago`
          : "Time unavailable";
      out += text(34, y + 21, age, 9, C.muted);
      // Separate per-unit bars are a magnitude cue, never an operational threshold.
      if (!simple(o) && numeric(c.delta))
        out += rect(
          205,
          y + 15,
          clamp(Math.abs(c.delta) / magnitudes.get(c.unit ?? ""), 0, 1) * 159,
          3,
          C.accent,
          1.5,
          'opacity=".4" data-motion="delta-bar"',
        );
    }
  });
  return out;
}

export function renderContext(id, data, o, context = {}) {
  void context;
  if (
    [
      "sparkline",
      "history",
      "depth-graph",
      "state-history",
      "comparison-bars",
      "multi-graph",
    ].includes(id)
  )
    return graphFace(id, data, o);
  switch (id) {
    case "anchor":
      return anchorFace(data, o);
    case "gps":
      return gpsFace(data, o);
    case "signal":
      return signalFace(data, o);
    case "board-health":
      return boardHealthFace(data, o);
    case "traffic":
      return trafficFace(data, o);
    case "route":
      return routeFace(data, o);
    case "forecast":
      return forecastFace(data, o);
    case "tide":
      return tideFace(data, o);
    case "moon":
      return moonFace(data, o);
    case "pressure":
      return pressureFace(data, o);
    case "power-budget":
      return powerFace(data, o);
    case "engine-vitals":
      return engineFace(data, o);
    case "tanks-budget":
      return tanksFace(data, o);
    case "water-budget":
      return waterFace(data, o);
    case "depth-safety":
      return depthSafetyFace(data, o);
    case "environment":
      return environmentFace(data, o);
    case "changes":
      return changesFace(data, o);
    default:
      throw new Error(`Unknown context renderer ${id}`);
  }
}
